import { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { useCommonTemplateItems, useUpdateCommonTemplateItems } from '@/hooks/useCommonTemplateItems';
import { EditableItemList } from '../breakdown-templates/components/EditableItemList';
import type { EditableItem } from '../breakdown-templates/components/EditableItemRow';

export default function CommonLineItems() {
  const { data: items = [], isLoading, isError } = useCommonTemplateItems();
  const updateMutation = useUpdateCommonTemplateItems();

  const [commonLineItems, setCommonLineItems] = useState<EditableItem[]>([]);
  const [reusableLineItems, setReusableLineItems] = useState<EditableItem[]>([]);
  const [disclaimers, setDisclaimers] = useState<EditableItem[]>([]);

  // Sync from API data
  useEffect(() => {
    const common: EditableItem[] = [];
    const reusable: EditableItem[] = [];
    const disc: EditableItem[] = [];

    items.forEach((item) => {
      const editItem: EditableItem = {
        id: item.id,
        category: item.category,
        description: item.description ?? '',
      };
      if (item.isDisclaimer) {
        disc.push(editItem);
      } else if (item.isReusable) {
        reusable.push(editItem);
      } else {
        common.push(editItem);
      }
    });

    setCommonLineItems(common);
    setReusableLineItems(reusable);
    setDisclaimers(disc);
  }, [items]);

  const saveAll = useCallback(
    async (common: EditableItem[], reusable: EditableItem[], disc: EditableItem[]) => {
      let sortOrder = 0;
      const allItems = [
        ...common.map((item) => ({
          id: item.id,
          category: item.category,
          description: item.description,
          isReusable: false,
          isDisclaimer: false,
          sortOrder: sortOrder++,
        })),
        ...reusable.map((item) => ({
          id: item.id,
          category: item.category,
          description: item.description,
          isReusable: true,
          isDisclaimer: false,
          sortOrder: sortOrder++,
        })),
        ...disc.map((item) => ({
          id: item.id,
          category: item.category,
          description: item.description,
          isReusable: false,
          isDisclaimer: true,
          sortOrder: sortOrder++,
        })),
      ];

      await updateMutation.mutateAsync({ items: allItems });
    },
    [updateMutation]
  );

  // --- Common Line Item handlers ---
  const handleAddCommon = useCallback(
    async (data: { category: string; description: string }) => {
      const newItem: EditableItem = { id: crypto.randomUUID(), category: data.category, description: data.description };
      const updated = [...commonLineItems, newItem];
      setCommonLineItems(updated);
      await saveAll(updated, reusableLineItems, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleEditCommon = useCallback(
    async (editedItem: EditableItem) => {
      const updated = commonLineItems.map((item) => (item.id === editedItem.id ? editedItem : item));
      setCommonLineItems(updated);
      await saveAll(updated, reusableLineItems, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleDeleteCommon = useCallback(
    async (id: string) => {
      const updated = commonLineItems.filter((item) => item.id !== id);
      setCommonLineItems(updated);
      await saveAll(updated, reusableLineItems, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleReorderCommon = useCallback(
    async (reordered: EditableItem[]) => {
      setCommonLineItems(reordered);
      await saveAll(reordered, reusableLineItems, disclaimers);
    },
    [reusableLineItems, disclaimers, saveAll]
  );

  // Move item from Common → Reusable
  const handleMoveToReusable = useCallback(
    (id: string) => {
      const item = commonLineItems.find((i) => i.id === id);
      if (!item) return;
      const updatedCommon = commonLineItems.filter((i) => i.id !== id);
      const updatedReusable = [...reusableLineItems, item];
      setCommonLineItems(updatedCommon);
      setReusableLineItems(updatedReusable);
      saveAll(updatedCommon, updatedReusable, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  // Move item from Reusable → Common
  const handleMoveToCommon = useCallback(
    (id: string) => {
      const item = reusableLineItems.find((i) => i.id === id);
      if (!item) return;
      const updatedReusable = reusableLineItems.filter((i) => i.id !== id);
      const updatedCommon = [...commonLineItems, item];
      setReusableLineItems(updatedReusable);
      setCommonLineItems(updatedCommon);
      saveAll(updatedCommon, updatedReusable, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  // --- Reusable Line Item handlers ---
  const handleAddReusable = useCallback(
    async (data: { category: string; description: string }) => {
      const newItem: EditableItem = { id: crypto.randomUUID(), category: data.category, description: data.description };
      const updated = [...reusableLineItems, newItem];
      setReusableLineItems(updated);
      await saveAll(commonLineItems, updated, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleEditReusable = useCallback(
    async (editedItem: EditableItem) => {
      const updated = reusableLineItems.map((item) => (item.id === editedItem.id ? editedItem : item));
      setReusableLineItems(updated);
      await saveAll(commonLineItems, updated, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleDeleteReusable = useCallback(
    async (id: string) => {
      const updated = reusableLineItems.filter((item) => item.id !== id);
      setReusableLineItems(updated);
      await saveAll(commonLineItems, updated, disclaimers);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleReorderReusable = useCallback(
    async (reordered: EditableItem[]) => {
      setReusableLineItems(reordered);
      await saveAll(commonLineItems, reordered, disclaimers);
    },
    [commonLineItems, disclaimers, saveAll]
  );

  // --- Disclaimer handlers ---
  const handleAddDisclaimer = useCallback(
    async (data: { category: string; description: string }) => {
      const newItem: EditableItem = { id: crypto.randomUUID(), category: 'Disclaimer', description: data.description };
      const updated = [...disclaimers, newItem];
      setDisclaimers(updated);
      await saveAll(commonLineItems, reusableLineItems, updated);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleEditDisclaimer = useCallback(
    async (editedItem: EditableItem) => {
      const updated = disclaimers.map((item) => (item.id === editedItem.id ? editedItem : item));
      setDisclaimers(updated);
      await saveAll(commonLineItems, reusableLineItems, updated);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleDeleteDisclaimer = useCallback(
    async (id: string) => {
      const updated = disclaimers.filter((item) => item.id !== id);
      setDisclaimers(updated);
      await saveAll(commonLineItems, reusableLineItems, updated);
    },
    [commonLineItems, reusableLineItems, disclaimers, saveAll]
  );

  const handleReorderDisclaimers = useCallback(
    async (reordered: EditableItem[]) => {
      setDisclaimers(reordered);
      await saveAll(commonLineItems, reusableLineItems, reordered);
    },
    [commonLineItems, reusableLineItems, saveAll]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-96 text-red-500">
        <p>Failed to load common line items. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList className="h-6 w-6 text-gray-700" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Common Line Items</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage the line items and disclaimers available in the breakdown editor.
            Common items can only be added once per job. Reusable items can be added multiple times.
          </p>
        </div>
      </div>

      <div className="space-y-8">
        <EditableItemList
          title="Common Line Items"
          items={commonLineItems}
          isSaving={updateMutation.isPending}
          onMoveItem={handleMoveToReusable}
          moveLabel="Move to Reusable"
          onAdd={handleAddCommon}
          onEdit={handleEditCommon}
          onDelete={handleDeleteCommon}
          onReorder={handleReorderCommon}
        />

        <EditableItemList
          title="Reusable Line Items"
          items={reusableLineItems}
          isSaving={updateMutation.isPending}
          onMoveItem={handleMoveToCommon}
          moveLabel="Move to Common"
          onAdd={handleAddReusable}
          onEdit={handleEditReusable}
          onDelete={handleDeleteReusable}
          onReorder={handleReorderReusable}
        />

        <EditableItemList
          title="Disclaimers"
          items={disclaimers}
          isDisclaimer
          isSaving={updateMutation.isPending}
          onAdd={handleAddDisclaimer}
          onEdit={handleEditDisclaimer}
          onDelete={handleDeleteDisclaimer}
          onReorder={handleReorderDisclaimers}
        />
      </div>
    </div>
  );
}
